/**
 * SaveIndicator — small status chip showing auto-save state.
 *
 * - "idle"   → invisible (returns null) unless lastSavedAt is set: shows "✓ Guardado hace 3s"
 * - "saving" → spinner + "Guardando…"
 * - "saved"  → green check + "Guardado"
 * - "error"  → red dot + "Error al guardar" + retry button
 */
import { useEffect, useState } from "react";
import { Check, Loader2, AlertCircle, RefreshCw } from "lucide-react";

function relativeTimeEs(d) {
  if (!d) return "";
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 5) return "hace un instante";
  if (s < 60) return `hace ${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  return d.toLocaleString();
}

function relativeTimeEn(d) {
  if (!d) return "";
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return d.toLocaleString();
}

export default function SaveIndicator({ status, lastSavedAt, onRetry, lang = "es", className = "" }) {
  // Tick once a second so the relative time stays fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const i = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(i);
  }, [lastSavedAt]);

  const rel = lang === "es" ? relativeTimeEs(lastSavedAt) : relativeTimeEn(lastSavedAt);
  const base = `inline-flex items-center gap-1.5 text-xs ${className}`;

  if (status === "saving") {
    return (
      <span className={`${base} text-[#5C5B5A]`} data-testid="save-indicator-saving">
        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
        {lang === "es" ? "Guardando…" : "Saving…"}
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className={`${base} text-[#9E2A2B]`} data-testid="save-indicator-error">
        <AlertCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
        {lang === "es" ? "Error al guardar" : "Save failed"}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-1 inline-flex items-center gap-1 underline underline-offset-2 hover:text-[#7d2122]"
            data-testid="save-indicator-retry-btn"
          >
            <RefreshCw className="h-3 w-3" strokeWidth={1.5} />
            {lang === "es" ? "Reintentar" : "Retry"}
          </button>
        )}
      </span>
    );
  }
  if (status === "saved" || lastSavedAt) {
    return (
      <span className={`${base} text-[#5C5B5A]`} data-testid="save-indicator-saved">
        <Check className="h-3.5 w-3.5 text-[#2563EB]" strokeWidth={2} />
        {lang === "es" ? "Guardado" : "Saved"}
        {lastSavedAt && (
          <span className="text-[#8A8886]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            · {rel}
          </span>
        )}
      </span>
    );
  }
  return null;
}
